import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { importGuestDraftIfAny } from "../lib/guestFlow";

/**
 * Handles Emergent Google OAuth return.
 * The provider redirects to /dashboard#session_id=xxx — we intercept synchronously
 * from AppRouter (see App.js) BEFORE ProtectedRoute checks /auth/me, exchange the
 * session_id for a session cookie, then navigate to /dashboard.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const processedRef = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const hash = location.hash || window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        await api.post("/auth/google/session", { session_id: sessionId });
        const imported = await importGuestDraftIfAny();
        toast.success(imported ? "Signed in — your draft is now your profile!" : "Signed in with Google");
        const dest = imported ? "/profile/edit" : "/dashboard";
        window.history.replaceState({}, "", dest);
        window.location.replace(dest);
      } catch (e) {
        setError(formatApiError(e));
        setTimeout(() => navigate("/login", { replace: true }), 2500);
      }
    })();
  }, [location, navigate]);

  return (
    <div className="container py-5 text-center" data-testid="auth-callback">
      <div className="sb-card mx-auto" style={{ maxWidth: 420 }}>
        <h2 className="h4">Signing you in…</h2>
        {error ? (
          <p className="text-danger mt-3 mb-0" data-testid="auth-callback-error">
            {error}
          </p>
        ) : (
          <p className="text-muted mt-3 mb-0">Just a moment while we connect your Google account.</p>
        )}
      </div>
    </div>
  );
}
